/**
 * Notification React Query Hooks
 *
 * TanStack Query hooks for notification operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { NotificationFilters } from '@/domain/notification'
import * as notificationService from './notification.service'
import { useAuth } from '@/application/auth'

// =============================================================================
// Query Keys
// =============================================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filters?: NotificationFilters) => [...notificationKeys.all, 'list', filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Get notifications list
 */
export function useNotifications(filters?: NotificationFilters) {
  const { isAuthenticated, isLoading } = useAuth()

  // Only fetch when fully authenticated (not loading and authenticated)
  const canFetch = isAuthenticated && !isLoading

  return useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: () => notificationService.getNotifications(filters),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: canFetch ? 1000 * 60 : false, // Only refetch when authenticated
    enabled: canFetch,
  })
}

/**
 * Get unread notification count
 * Used for the notification bell badge
 */
export function useUnreadCount() {
  const { isAuthenticated, isLoading } = useAuth()

  // Only fetch when fully authenticated (not loading and authenticated)
  const canFetch = isAuthenticated && !isLoading

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 1000 * 15, // 15 seconds
    refetchInterval: canFetch ? 1000 * 30 : false, // Only refetch when authenticated
    enabled: canFetch,
  })
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate both the list and the count
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Delete all notifications
 */
export function useDeleteAllNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationService.deleteAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}
